import React, {useContext, useEffect} from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography
} from "@mui/material";
import {
  CONTEXT_ACTION_TYPE,
  DataStoreContext,
  EquipmentConfigurationPayload,
  STEP_ORDERS,
  STEPS,
  useStep,
  validateStepOutput
} from "../../contexts/OperatingTestContext";
import {
  CALCULATED_SORT_ORDER,
  CALCULATED_SORT_ORDER_LABEL,
  CalculatedSortOrder
} from "../../../../constants/handselection";
import SearchCriteriaTable from "../tables/SearchCriteriaTable";
import {ArmDocument} from "../../../equipmentdb/types/pj1dbApiArm";
import {AttachmentDocument} from "../../../equipmentdb/types/pj1dbApiAttachment";
import {mergeStatuses, Status, STATUSES} from "../../../../hooks/useFetchAllQuery";
import {hasFingertipIdResult, isConnectable, sortCalculationResults} from "../../../../utils/handselectionUtil";
import {CalcHandCandidatePoolResult, HandResultItem, Result} from "../../../../types/pj1dbApiHandling";
import {HandDocument} from "../../../equipmentdb/types/pj1dbApiHand";
import HandListItem from "../lists/HandListItem";
import {FingertipDocument} from "../../../equipmentdb/types/pj1dbApiFingertip";
import {useHand} from "../../hooks/useHand";
import {useFingertip} from "../../hooks/useFingertip";
import {useConnectableAttachment} from "../../hooks/useConnectableAttachment";
import {Dict} from "../../../../types/pj1dbApi";
import useConfirmDialog from "../../../../components/ConfirmDialog";

export default function EquipmentConfigurationStep() {
  if (!useIsShow()) {
    throw new Error('EquipmentConfigurationStep is not shown');
  }

  const {state, dispatch} = useContext(DataStoreContext);
  const [, setStep] = useStep();
  const {ConfirmDialog, openConfirmDialog} = useConfirmDialog();
  useData();

  const isLoading = state.equipmentStep.searchStatus === STATUSES.FETCHING;
  const isError = state.equipmentStep.searchStatus === STATUSES.FAILURE;
  const dataExists = (state.searchCriteriaStep.calculationResults?.results?.length ?? 0) > 0;
  const selectHand = (data: EquipmentConfigurationPayload['selectedData']) => {
    dispatch({
      type: CONTEXT_ACTION_TYPE.EQUIPMENT_CONFIGURATION,
      payload: {
        selectedData: data,
      } satisfies EquipmentConfigurationPayload
    });
    setStep(STEP_ORDERS[STEPS.EQUIPMENT_CONFIGURATION].NEXT);
  }

  return (
    <Stack width='100%' gap={4}>
      <ConfirmDialog
        title="機器構成の選択"
        message="アームとハンドの接続が保証されません。アタッチメントの追加登録を検討してください。"
        confirmButton="接続できることを確認済み"
        confirmButtonStyle={undefined}
      />

      <Box width='100%'>
        <Box display='flex' justifyContent='space-between'>
          <Typography variant='h6' component='h2'>計算条件</Typography>
          <Button
            sx={{py: 0}}
            onClick={() => {
              setStep(STEP_ORDERS[STEPS.EQUIPMENT_CONFIGURATION].PREV);
            }}
            variant='text'
            color='inherit'
          >計算条件の変更</Button>
        </Box>

        <SearchCriteriaTable
          criteria={state.searchCriteriaStep.searchCriteriaList}
          handPowerSources={state.searchCriteriaStep.handPowerSourceInputs.handPowerSource}
          selectedArm={state.searchCriteriaStep.selectedArm}
        />
      </Box>

      <Stack width='100%' gap={2}>
        <Box display='flex' justifyContent='space-between'>
          <Typography variant='h6' component='h2'>計算結果一覧</Typography>
          <FormControl sx={{minWidth: 120}} size="small">
            <InputLabel id="sort-order-label">並び順</InputLabel>
            <Select
              labelId="sort-order-label"
              name="sortOrder"
              label="並び順"
              value={state.equipmentStep.sortOrder}
              onChange={(e) => {
                dispatch({
                  type: CONTEXT_ACTION_TYPE.EQUIPMENT_CONFIGURATION,
                  payload: {
                    sortOrder: e.target.value as CalculatedSortOrder
                  } satisfies EquipmentConfigurationPayload
                });
              }}
              sx={{bgcolor: "white"}}
            >
              {Object.values(CALCULATED_SORT_ORDER).map((value) => (
                <MenuItem key={value} value={value}>{CALCULATED_SORT_ORDER_LABEL[value]}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* ページ切り替えで再ロードされるので、ロード中でもすでにデータがあればローディングにしない */}
        {isLoading && !dataExists && (
          <Box display='flex' mt={2}>
            <CircularProgress/>
          </Box>
        )}

        {/* ページ切り替えで再ロードされるので、ロード中でもすでにデータがあれば表示するようにしている */}
        {dataExists && (
          <HandList
            calculateResult={state.searchCriteriaStep.calculationResults!}
            sortOrder={state.equipmentStep.sortOrder}
            selectedArm={state.searchCriteriaStep.selectedArm!}
            hands={state.equipmentStep.hands.flatMap((hand) => Object.values(hand))}
            fingertips={state.equipmentStep.fingertips.flatMap((fingertip) => Object.values(fingertip))}
            attachments={state.equipmentStep.connectableAttachments
              .flatMap((attachment) => Object.values(attachment))
            }
            status={state.equipmentStep.searchStatus}
            onSelectConnectable={selectHand}
            onSelectUnconnectable={(data) => {
              openConfirmDialog().then((r) => {
                if (r === 'confirm') {
                  selectHand(data);
                }
              }).catch(console.error);
            }}
          />)}

        {!dataExists && !isLoading && !isError && (
          <Box width='100%' textAlign='center'>
            <Typography variant="subtitle1" sx={{fontWeight: "bold"}}
                        dangerouslySetInnerHTML={{__html: '該当データがありません。'}}/>
          </Box>
        )}
        {isError && (
          <Box width='100%' textAlign='center'>
            <Typography
              variant="subtitle1"
              sx={{fontWeight: "bold"}}
              dangerouslySetInnerHTML={{__html: '検索処理が失敗しました。'}}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
}

function useIsShow() {
  const {state} = useContext(DataStoreContext);
  const [step] = useStep();

  return STEPS.EQUIPMENT_CONFIGURATION === step &&
    validateStepOutput(state, STEP_ORDERS[STEPS.EQUIPMENT_CONFIGURATION].PREV);
}

function useData() {
  const {state, dispatch} = useContext(DataStoreContext);
  const dummyHandleChange = () => {
  };
  const handIds = Array.from(new Set(state.searchCriteriaStep.calculationResults?.results
    .flatMap((result) => result.hands.map(h => h.handID)) ?? []));

  const hands = useHand({
    enableAutoFetch: true,
    onChange: dummyHandleChange,
    handIds,
  })
  const fingertips = useFingertip({
    enableAutoFetch: true,
    onChange: dummyHandleChange,
    fingertipIds: state.searchCriteriaStep.calculationResults?.results
      .flatMap(r => r.hands)
      .filter(hasFingertipIdResult)
      .map((result) => result.fingertipID) ?? [],
  })
  const attachments = useConnectableAttachment({
    enableAutoFetch: true,
    onChange: dummyHandleChange,
    handIds,
    armId: state.searchCriteriaStep.selectedArm?.attr.armId ?? '',
  })
  console.log('handIDs', handIds);

  const searchStatus = mergeStatuses(hands.searchStatus, fingertips.searchStatus, attachments.searchStatus);
  useEffect(() => {
    console.log('useData', hands.data, fingertips.data, attachments.data);
    dispatch({
      type: CONTEXT_ACTION_TYPE.EQUIPMENT_CONFIGURATION,
      payload: {
        searchStatus,
        hands: hands.data,
        fingertips: fingertips.data,
        connectableAttachments: attachments.data
          .filter((attachment): attachment is Dict<AttachmentDocument> => attachment !== undefined),
      } satisfies EquipmentConfigurationPayload
    })
  }, [
    dispatch,
    searchStatus,
    hands.data,
    fingertips.data,
    attachments.data,
  ]);
}

function HandList({selectedArm, ...props}: Omit<BaseHandListProps, 'onSelect' | 'calculateResult'> & {
  calculateResult: CalcHandCandidatePoolResult;
  selectedArm: ArmDocument,
  attachments: AttachmentDocument[],
  status: Status
  sortOrder: CalculatedSortOrder,
  onSelectConnectable: BaseHandListProps['onSelect'],
  onSelectUnconnectable: BaseHandListProps['onSelect'],
}) {
  const converted = sortCalculationResults(props.calculateResult.results.map((r) => ({
    ...r,
    hands: r.hands.filter((h) => firstHandById(h.handID, props.hands) !== null),
  })), props.sortOrder);
  const connectable = converted
    .flatMap((result) => result.hands)
    .filter((hand) => isConnectable(hand.handID, selectedArm.attr.armId ?? '', props.attachments));
  const unconnected = converted
    .flatMap((result) => result.hands)
    .filter((hand) => !isConnectable(hand.handID, selectedArm?.attr.armId ?? '', props.attachments));

  if (props.status === STATUSES.FETCHING) {
    return (
      <Box display='flex' justifyContent='center'>
        <CircularProgress/>
      </Box>
    )
  }

  return (
    <Stack gap={4}>
      <Stack gap={1}>
        <Typography variant='subtitle1' fontWeight='bold' component='h3'>接続可能ハンド一覧</Typography>
        {
          connectable.length === 0
            ? <Typography variant='body1' component='h3'>該当データがありません。</Typography>
            : <BaseHandList
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
              onSelect={props.onSelectConnectable}
              calculateResult={connectable}
            />
        }
      </Stack>
      <Stack gap={1}>
        <Typography variant='subtitle1' fontWeight='bold' component='h3'>接続不可能ハンド一覧</Typography>
        {
          unconnected.length === 0
            ? <Typography variant='body1' component='h3'>該当データがありません。</Typography>
            : <BaseHandList
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
              onSelect={props.onSelectUnconnectable}
              calculateResult={unconnected}/>
        }
      </Stack>
    </Stack>
  )
}

interface BaseHandListProps {
  calculateResult: HandResultItem[];
  hands: HandDocument[];
  fingertips: FingertipDocument[];
  onSelect: (data: {
    suggestionPattern: HandResultItem,
    hand: HandDocument,
    fingertip: FingertipDocument | null,
  }) => void;
}

function BaseHandList(props: BaseHandListProps) {
  const uniqueResults = Array.from(
    new Set(props.calculateResult.map(r => JSON.stringify(r)))
  ).map(r => JSON.parse(r) as HandResultItem);

  return (
    <Box display='flex' flexWrap='wrap' gap={2}>
      {uniqueResults.map((result) => {
        const hand = firstHandById(result.handID, props.hands);
        if (hand === null) {
          throw new Error('事前にハンドが存在するデータのみに絞り込んでください。')
        }

        return (
          <HandListItem
            key={JSON.stringify(result)}
            suggestionPattern={result}
            hand={hand}
            fingertip={firstFingertipById(
              'fingertipID' in result
                ? result.fingertipID
                : '',
              props.fingertips
            )}
            onSelect={props.onSelect}
          />
        )
      })}
    </Box>
  )
}

function firstHandById(handId: string, hands: HandDocument[]): HandDocument | null {
  return hands.find((hand) => hand.attr.handId === handId) ?? null;
}

function firstFingertipById(fingertipId: string, fingertips: FingertipDocument[]): FingertipDocument | null {
  return fingertips.find((fingertip) => fingertip.attr.fingertipId === fingertipId) ?? null;
}
